import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Paper,
  Backdrop,
} from '@mui/material';
import { Close, NavigateNext, NavigateBefore } from '@mui/icons-material';

const tourSteps = [
  {
    title: 'Welcome to PaceEstate!',
    content: 'Let\'s take a quick tour to help you get started with the platform. This will only take a minute.',
    target: null,
  },
  {
    title: 'Navigation Menu',
    content: 'Access all features from the left sidebar. Your most-used pages include Sales, Inventory, Leads, and Calendar.',
    target: 'nav',
    position: 'right',
  },
  {
    title: 'Dashboard',
    content: 'Your dashboard shows key metrics at a glance: active sales, revenue, pending invoices, and active leads. It\'s your home base.',
    target: 'dashboard',
    position: 'center',
  },
  {
    title: 'Global Search',
    content: 'Quickly find any sale, lead, inventory item, or invoice using the search icon. Try Ctrl+K or Cmd+K as a shortcut.',
    target: 'search',
    position: 'bottom',
  },
  {
    title: 'Notifications',
    content: 'Stay updated with the notification center. You\'ll see new leads, upcoming appointments, payment reminders, and important alerts.',
    target: 'notifications',
    position: 'bottom',
  },
  {
    title: 'Theme Toggle',
    content: 'Switch between light and dark mode for comfortable viewing in any environment.',
    target: 'theme',
    position: 'bottom',
  },
  {
    title: 'Help Center',
    content: 'Need assistance? Click the help icon anytime to access guides, articles, and search for answers. You can also press Ctrl+/ or Cmd+/.',
    target: 'help',
    position: 'bottom',
  },
  {
    title: 'Your Profile',
    content: 'Access your account settings, update your profile, and log out from the profile menu.',
    target: 'profile',
    position: 'bottom-left',
  },
  {
    title: 'You\'re All Set!',
    content: 'You\'re ready to start using PaceEstate. Remember, you can always access the Help Center if you need guidance. Good luck with your estate sales!',
    target: null,
  },
];

const OnboardingTour = () => {
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourCompleted, setTourCompleted] = useState(false);

  useEffect(() => {
    // Check if user has completed the tour
    const completed = localStorage.getItem('onboardingTourCompleted');
    if (!completed) {
      // Show tour after a short delay to let the page load
      setTimeout(() => {
        setShowTour(true);
      }, 1000);
    } else {
      setTourCompleted(true);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem('onboardingTourCompleted', 'true');
    setShowTour(false);
    setTourCompleted(true);
  };

  // Function to reset tour (for testing or if user wants to see it again)
  const resetTour = () => {
    localStorage.removeItem('onboardingTourCompleted');
    setTourCompleted(false);
    setCurrentStep(0);
    setShowTour(true);
  };

  // Expose resetTour globally for admin use
  useEffect(() => {
    window.resetOnboardingTour = resetTour;
    return () => {
      delete window.resetOnboardingTour;
    };
  }, []);

  if (!showTour || tourCompleted) {
    return null;
  }

  const step = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <>
      <Backdrop
        open={showTour}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      />
      <Dialog
        open={showTour}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            m: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Step {currentStep + 1} of {tourSteps.length}
            </Typography>
            <IconButton size="small" onClick={handleSkip} title="Skip tour">
              <Close />
            </IconButton>
          </Box>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mb: 3, height: 6, borderRadius: 1 }}
          />
        </Box>

        <DialogContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {step.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {step.content}
          </Typography>

          {step.target && (
            <Paper
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: 'primary.light',
                border: '2px dashed',
                borderColor: 'primary.main',
              }}
            >
              <Typography variant="body2" color="primary.dark">
                Look for this feature in the {step.position} of the screen
              </Typography>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleSkip} color="inherit">
            Skip Tour
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            startIcon={<NavigateBefore />}
          >
            Previous
          </Button>
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={currentStep < tourSteps.length - 1 ? <NavigateNext /> : null}
          >
            {currentStep < tourSteps.length - 1 ? 'Next' : 'Get Started'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OnboardingTour;
